import {
  useFormField
} from "./chunk-MJ3U6SP3.js";
import {
  cn,
  twVariants
} from "./chunk-LJ5F4WFH.js";

// src/Input/Input.tsx
import React2 from "react";

// src/Input/InputAddon.tsx
import * as React from "react";
var inputAddonStyles = twVariants({
  base: "flex flex-shrink-0 items-center justify-center",
  variants: {
    size: {
      sm: "[&_button.icon]:w-6 [&_button]:h-6",
      md: "[&_button.icon]:w-8 [&_button]:h-8",
      lg: "[&_button.icon]:w-10 [&_button]:h-10"
    }
  }
});
var InputAddon = React.forwardRef(
  ({ className, size = "md", ...props }, ref) => {
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        className: cn(inputAddonStyles({ size }), className),
        ref,
        ...props
      }
    );
  }
);
InputAddon.displayName = "InputAddon";
function useInputAddons(children) {
  const leftAddons = React.useMemo(
    () => React.Children.toArray(children).filter(
      (o) => React.isValidElement(o) && o.type === InputAddon && o.props.placement === "left"
    ),
    [children]
  );
  const rightAddons = React.useMemo(
    () => React.Children.toArray(children).filter(
      (o) => React.isValidElement(o) && o.type === InputAddon && (!o.props.placement || o.props.placement === "right")
    ),
    [children]
  );
  const placement = React.useMemo(
    () => leftAddons.length && rightAddons.length ? "both" : leftAddons.length ? "left" : rightAddons.length ? "right" : "none",
    [leftAddons, rightAddons]
  );
  return {
    leftAddons,
    rightAddons,
    placement
  };
}

// src/Input/Input.tsx
var inputStyles = twVariants({
  slots: {
    rootContainer: [
      "relative flex w-full rounded-lg border border-neutral bg-surface-lighter text-neutral-primary transition-all",
      "focus-within:border-brand-selected focus:outline-0",
      "disabled:text-neutral-disabled",
      "group-[.input-group]:rounded-none group-[.input-group]:border-0 group-[.input-group]:px-0"
    ],
    root: [
      "h-full w-full bg-surface-lighter placeholder:text-neutral-secondary",
      "disabled:placeholder:text-neutral-disabled",
      "focus-visible:outline-none focus-visible:ring-0"
    ],
    // translate -1px because there is a border of 1.
    rootIcon: ["pointer-events-none absolute -translate-x-px -translate-y-px"]
  },
  variants: {
    placement: {
      left: {
        rootIcon: "left-3"
      },
      right: {
        rootIcon: "right-3"
      }
    },
    size: {
      sm: {
        rootContainer: "h-8 text-body-1 group-[.input-group]:h-full",
        root: "text-body-1",
        rootIcon: "top-2 h-4 w-4"
      },
      md: {
        rootContainer: "h-10 text-body-1 group-[.input-group]:h-full",
        root: "text-body-1",
        rootIcon: "top-2.5 h-5 w-5"
      },
      lg: {
        rootContainer: "h-13 text-body-1 group-[.input-group]:h-full",
        root: "text-body-1",
        rootIcon: "top-3.5 h-6 w-6"
      }
    },
    addonPlacement: {
      none: {
        rootContainer: "px-3",
        root: ""
      },
      left: {
        rootContainer: "pl-1.5 pr-3",
        root: "ml-1.5"
      },
      right: {
        rootContainer: "pl-3 pr-1.5",
        root: "mr-1.5"
      },
      both: {
        rootContainer: "px-1.5",
        root: "mx-1.5"
      }
    },
    disabled: {
      true: {
        rootContainer: "cursor-not-allowed border-transparent text-neutral-disabled",
        root: "cursor-not-allowed"
      }
    },
    readOnly: {
      true: {
        rootContainer: "focus-within:border-inherit bg-white",
        root: "bg-white"
      }
    },
    invalid: {
      true: {
        rootContainer: "border-alert-danger focus-within:border-alert-danger"
      }
    }
  },
  compoundVariants: [
    {
      size: "sm",
      placement: "left",
      className: {
        root: "pl-6"
      }
    },
    {
      size: "md",
      placement: "left",
      className: {
        root: "pl-7"
      }
    },
    {
      size: "lg",
      placement: "left",
      className: {
        root: "pl-8"
      }
    },
    {
      size: "sm",
      placement: "right",
      className: {
        root: "pr-6"
      }
    },
    {
      size: "md",
      placement: "right",
      className: {
        root: "pr-7"
      }
    },
    {
      size: "lg",
      placement: "right",
      className: {
        root: "pr-8"
      }
    }
  ]
});
var Input = React2.forwardRef(
  ({ className, type, size = "md", leftIcon, rightIcon, children, ...props }, ref) => {
    const { leftAddons, rightAddons, placement } = useInputAddons(children);
    const ownProps = useFormField(props);
    const disabled = !!ownProps.disabled;
    const invalid = ownProps["aria-invalid"];
    const { rootContainer, root, rootIcon } = inputStyles({
      size,
      addonPlacement: placement,
      disabled,
      readOnly: !!ownProps.readOnly,
      invalid: !!invalid
    });
    return /* @__PURE__ */ React2.createElement("div", { className: cn(rootContainer(), className) }, leftAddons, leftIcon && /* @__PURE__ */ React2.createElement("span", { className: rootIcon({ placement: "left" }) }, leftIcon), /* @__PURE__ */ React2.createElement(
      "input",
      {
        ref,
        type,
        className: cn(
          root({
            placement: leftIcon ? "left" : rightIcon ? "right" : void 0
          })
        ),
        ...ownProps
      }
    ), rightIcon && /* @__PURE__ */ React2.createElement("span", { className: rootIcon({ placement: "right" }) }, rightIcon), rightAddons);
  }
);
Input.displayName = "Input";

export {
  inputAddonStyles,
  InputAddon,
  useInputAddons,
  inputStyles,
  Input
};
//# sourceMappingURL=chunk-YP5PP7SV.js.map